import { useEffect, useMemo, useRef, useState } from 'react';
// @mui
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
// utils
import axiosInstance from 'src/utils/axios';
import { APIS } from 'src/utils/serviceUrls';

// ---------------------------------------------------------------------------
// Mentor picker for a diploma batch. Mentors are plain USER accounts (NOT
// Instructor records), so they never surface on the public /instructors page.
// This searches the users list server-side (so we never load every user) and
// stores the chosen user ids on the batch.
//
// value/onChange deal in user-id strings (what the batch stores). The picker
// keeps a local id→user map for labels: seeded from the batch's already-chosen
// mentors (populated user objects on edit) and grown as options are searched
// and picked, so selected chips always show a name rather than a raw id.
// ---------------------------------------------------------------------------

type UserLite = { _id: string; name?: string; email?: string };

type Props = {
  value: string[];
  onChange: (ids: string[]) => void;
  // The batch's currently-assigned mentors, populated as user objects on edit,
  // so their chips render with names before any search runs.
  initialUsers?: UserLite[];
  /** A server error for this list (e.g. "not an existing user account"). */
  error?: string;
};

const labelFor = (u: UserLite) =>
  u.email ? `${u.name || u.email} — ${u.email}` : u.name || u._id;

export default function BatchMentorsPicker({ value, onChange, initialUsers, error }: Props) {
  const [options, setOptions] = useState<UserLite[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  // id -> user, for rendering the selected chips by name.
  const knownRef = useRef<Map<string, UserLite>>(new Map());

  // Seed the label map from the batch's already-chosen mentors.
  useEffect(() => {
    (initialUsers || []).forEach((u) => {
      if (u && u._id) knownRef.current.set(String(u._id), u);
    });
  }, [initialUsers]);

  // Debounced server-side search. Only fires for a non-trivial query so we
  // never pull the whole users table.
  useEffect(() => {
    const q = input.trim();
    if (q.length < 2) {
      setOptions([]);
      return undefined;
    }
    let alive = true;
    setLoading(true);
    const t = setTimeout(() => {
      axiosInstance
        .get(APIS.USERS.PAGINATED, { params: { search: q, pageSize: 10 } })
        .then((res) => {
          if (!alive) return;
          const data: any = res.data?.data ?? res.data;
          const items: UserLite[] = Array.isArray(data?.items)
            ? data.items
            : Array.isArray(data)
              ? data
              : [];
          items.forEach((u) => knownRef.current.set(String(u._id), u));
          setOptions(items);
        })
        .catch(() => alive && setOptions([]))
        .finally(() => alive && setLoading(false));
    }, 300);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [input]);

  // Selected user objects, resolved from ids via the label map (fallback to a
  // bare id so a chip still renders if the user isn't loaded yet).
  const selected = useMemo<UserLite[]>(
    () => (value || []).map((id) => knownRef.current.get(String(id)) || { _id: String(id) }),
    [value]
  );

  return (
    <Autocomplete
      multiple
      size="small"
      loading={loading}
      options={options}
      value={selected}
      // Server-side search — don't let MUI re-filter the returned options.
      filterOptions={(x) => x}
      isOptionEqualToValue={(opt, val) => String(opt._id) === String(val._id)}
      getOptionLabel={(u: any) => labelFor(u)}
      onInputChange={(_e, v) => setInput(v)}
      onChange={(_e, vals: any[]) => {
        vals.forEach((v) => v?._id && knownRef.current.set(String(v._id), v));
        onChange(vals.map((v) => String(v._id)));
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Mentors"
          placeholder="Search users by name or email"
          error={!!error}
          helperText={
            error ||
            'Not shown on the page. Can run classes that have no teacher, and see this batch in the instructor portal.'
          }
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
